/**
 * LegaPage — vista lega della stagione in corso.
 *
 * Andrea 2026-05-23: niente piu' dropdown stagione/competizione. La pagina
 * carica automaticamente la stagione attiva e mostra una tab-bar di 4
 * bottoni in base alle competizioni disponibili:
 *
 *   - Campionato                → classifica
 *   - Gironi Coppa              → composizione gironi
 *   - Fase eliminazione Coppa   → partite quarti / semi / finale
 *   - Supercoppa                → singola partita di supercoppa
 *
 * Ogni bottone appare solo se la rispettiva competizione esiste. Default
 * selezionato: il primo disponibile in quest'ordine.
 */

import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
  useClassifica,
  useCompetizioniStagione,
  useGironiCL,
  usePartiteCompetizione,
  usePianoKO,
  type CompetizioneItem,
  type FasePartita,
  type PartitaItem,
} from "../../hooks/useFase5";
import { useSquadre } from "../../hooks/useFase7";
import { useStagioneCorrente } from "../../hooks/useStagione";
import {
  classificaConTutteLeSquadre,
  classificaDaPartite,
  squadreDaPartite,
} from "../common/classifica";
import { MobileShell } from "../shell/MobileShell";
import { ClassificaTabella } from "./ClassificaTabella";
import { TabelloneCL } from "./TabelloneCL";
import { tr } from "../../i18n";

type Tab = "campionato" | "gironi" | "eliminazione" | "supercoppa";

const FASI_ELIMINAZIONE: FasePartita[] = [
  "quarti_andata",
  "quarti_ritorno",
  "semi_andata",
  "semi_ritorno",
  "finale_andata",
  "finale_ritorno",
  "finale",
];

function labelFase(fase: FasePartita): string {
  switch (fase) {
    case "quarti_andata":
      return tr("Quarti · andata");
    case "quarti_ritorno":
      return tr("Quarti · ritorno");
    case "semi_andata":
      return tr("Semifinale · andata");
    case "semi_ritorno":
      return tr("Semifinale · ritorno");
    case "finale_andata":
      return tr("Finale · andata");
    case "finale_ritorno":
      return tr("Finale · ritorno");
    case "finale":
      return tr("Finale");
    case "supercoppa":
      return tr("Supercoppa");
    default:
      return fase;
  }
}

function roundOfFase(fase: FasePartita): "quarti" | "semi" | "finale" | null {
  if (fase === "quarti_andata" || fase === "quarti_ritorno") return "quarti";
  if (fase === "semi_andata" || fase === "semi_ritorno") return "semi";
  if (fase === "finale" || fase === "finale_andata" || fase === "finale_ritorno") return "finale";
  return null;
}

export function LegaPage() {
  useAuth(); // session check
  const { data: stagione } = useStagioneCorrente();
  const stagioneId = stagione?.id ?? null;
  const { data: competizioni } = useCompetizioniStagione(stagioneId);
  const { data: squadre } = useSquadre();

  const squadraNomeById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of squadre ?? []) m.set(s.id, s.nome);
    return m;
  }, [squadre]);
  const nomeSquadra = (id: string): string =>
    squadraNomeById.get(id) ?? tr("{{v1}}…", { v1: id.slice(0, 8) });

  // Trova la competizione di ogni tipo (prendo la prima — di solito ce n'e' una sola per tipo)
  const campionato = competizioni?.find((c) => c.tipo === "campionato") ?? null;
  const championsLeague = competizioni?.find((c) => c.tipo === "champions_league") ?? null;
  const supercoppa = competizioni?.find((c) => c.tipo === "supercoppa") ?? null;

  const hasCampionato = !!campionato;
  const hasCL = !!championsLeague;
  const hasSupercoppa = !!supercoppa;

  // Default tab: prima disponibile in quest'ordine
  const defaultTab: Tab = hasCampionato
    ? "campionato"
    : hasCL
      ? "gironi"
      : hasSupercoppa
        ? "supercoppa"
        : "campionato";
  const [tab, setTab] = useState<Tab>(defaultTab);

  return (
    <MobileShell logoSuffix="LEGA">
      <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Lega")}</h2>

      {!stagione && (
        <div className="card text-center text-fg-muted">{tr("Nessuna stagione attiva.")}</div>
      )}

      {stagione && (
        <>
          {/* Tab-bar bottoni — visibile solo per le competizioni esistenti */}
          <div className="flex gap-2 flex-wrap">
            {hasCampionato && (
              <TabButton active={tab === "campionato"} onClick={() => setTab("campionato")}>
                {tr("📊 Campionato")}
              </TabButton>
            )}
            {hasCL && (
              <TabButton active={tab === "gironi"} onClick={() => setTab("gironi")}>
                {tr("🏆 Gironi Coppa")}
              </TabButton>
            )}
            {hasCL && (
              <TabButton active={tab === "eliminazione"} onClick={() => setTab("eliminazione")}>
                {tr("⚔️ Fase eliminazione Coppa")}
              </TabButton>
            )}
            {hasSupercoppa && (
              <TabButton active={tab === "supercoppa"} onClick={() => setTab("supercoppa")}>
                {tr("🥇 Supercoppa")}
              </TabButton>
            )}
          </div>

          {tab === "campionato" && campionato && (
            <ClassificaSection competizione={campionato} nomeSquadra={nomeSquadra} />
          )}

          {tab === "gironi" && championsLeague && (
            <GironiSection competizioneId={championsLeague.id} nomeSquadra={nomeSquadra} />
          )}

          {tab === "eliminazione" && championsLeague && (
            <FaseEliminazioneSection
              competizioneId={championsLeague.id}
              nomeSquadra={nomeSquadra}
            />
          )}

          {tab === "supercoppa" && supercoppa && (
            <SupercoppaSection competizioneId={supercoppa.id} nomeSquadra={nomeSquadra} />
          )}
        </>
      )}
    </MobileShell>
  );
}

// === Sotto-componenti ====================================================

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`pill ${
        active ? "bg-accent text-ink font-bold" : "bg-primary-deep/40 text-fg-muted"
      }`}
    >
      {children}
    </button>
  );
}

function ClassificaSection({
  competizione,
  nomeSquadra,
}: {
  competizione: CompetizioneItem;
  nomeSquadra: (id: string) => string;
}) {
  const { data: classifica } = useClassifica(competizione.id);
  const { data: partite } = usePartiteCompetizione(competizione.id);
  const { data: squadre } = useSquadre();
  // Andrea 2026-09-09: niente piu' "classifica vuota". Le squadre si
  // leggono dal calendario (esiste da prima che si giochi) e chi non ha
  // ancora partite compare a zero, come in home.
  const righe = useMemo(() => {
    const ids = squadreDaPartite(partite);
    return classificaConTutteLeSquadre(
      classifica,
      ids.length > 0 ? ids : (squadre ?? []).map((sq) => sq.id),
      nomeSquadra,
    );
  }, [classifica, partite, squadre, nomeSquadra]);

  return <ClassificaTabella righe={righe} nomeSquadra={nomeSquadra} />;
}

function GironiSection({
  competizioneId,
  nomeSquadra,
}: {
  competizioneId: string;
  nomeSquadra: (id: string) => string;
}) {
  const { data: gironi } = useGironiCL(competizioneId);
  const { data: partite } = usePartiteCompetizione(competizioneId);
  const { data: pianoKO } = usePianoKO(competizioneId);

  // Quante passano il turno: con i quarti in tabellone qualificano tutte
  // e quattro (i gironi fanno da sorteggio), altrimenti le prime due.
  const qualificate = (pianoKO ?? []).some((t) => t.fase === "quarti") ? 4 : 2;

  if (!gironi || gironi.length === 0) {
    return <div className="card text-center text-fg-muted">{tr("Gironi non disponibili.")}</div>;
  }

  // Andrea 2026-09-10: gironi uno SOTTO l'altro anche su desktop —
  // affiancati la colonna squadra si stringe e i nomi vengono tagliati.
  return (
    <div className="space-y-4">
      {gironi.map((g) => {
        const fase: FasePartita = g.nome === "A" ? "girone_a" : "girone_b";
        const righe = classificaDaPartite(
          g.partecipanti.map((pa) => pa.squadra_id),
          (partite ?? []).filter((pa) => pa.fase === fase),
          nomeSquadra,
        );
        const teste = g.partecipanti
          .filter((pa) => pa.testa_di_serie)
          .map((pa) => nomeSquadra(pa.squadra_id));
        return (
          <div key={g.id} className="space-y-1">
            <ClassificaTabella
              titolo={tr("Girone {{v1}}", { v1: g.nome })}
              righe={righe}
              nomeSquadra={nomeSquadra}
              qualificate={qualificate < g.partecipanti.length ? qualificate : undefined}
            />
            {teste.length > 0 && (
              <div className="text-[10px] text-fg-muted px-1">
                ⭐ Teste di serie: {teste.join(", ")}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FaseEliminazioneSection({
  competizioneId,
  nomeSquadra,
}: {
  competizioneId: string;
  nomeSquadra: (id: string) => string;
}) {
  const { data: partite } = usePartiteCompetizione(competizioneId);
  const { data: gironi } = useGironiCL(competizioneId);

  // Filtra solo le fasi a eliminazione, e raggruppa per round
  const partitePerRound = useMemo(() => {
    const buckets: Record<"quarti" | "semi" | "finale", PartitaItem[]> = {
      quarti: [],
      semi: [],
      finale: [],
    };
    for (const p of partite ?? []) {
      if (!FASI_ELIMINAZIONE.includes(p.fase)) continue;
      const r = roundOfFase(p.fase);
      if (r) buckets[r].push(p);
    }
    // Ordina ogni bucket: andata prima del ritorno
    const faseOrder: Record<string, number> = {
      quarti_andata: 0,
      quarti_ritorno: 1,
      semi_andata: 0,
      semi_ritorno: 1,
      finale_andata: 0,
      finale_ritorno: 1,
      finale: 2,
    };
    for (const k of Object.keys(buckets) as Array<keyof typeof buckets>) {
      buckets[k].sort((a, b) => (faseOrder[a.fase] ?? 99) - (faseOrder[b.fase] ?? 99));
    }
    return buckets;
  }, [partite]);

  const hasQuarti = partitePerRound.quarti.length > 0;
  const hasSemi = partitePerRound.semi.length > 0;
  const hasFinale = partitePerRound.finale.length > 0;

  return (
    <div className="space-y-4">
      {/* Tabellone ad albero: c'e' da subito, coi posti generici. */}
      <TabelloneCL
        competizioneId={competizioneId}
        gironi={gironi}
        partite={partite ?? []}
        nomeSquadra={nomeSquadra}
      />

      {/* Sotto, l'elenco gara per gara delle fasi sorteggiate. */}
      {hasQuarti && (
        <RoundBlock
          titolo="Quarti di finale"
          partite={partitePerRound.quarti}
          nomeSquadra={nomeSquadra}
        />
      )}
      {hasSemi && (
        <RoundBlock titolo="Semifinali" partite={partitePerRound.semi} nomeSquadra={nomeSquadra} />
      )}
      {hasFinale && (
        <RoundBlock titolo="Finale" partite={partitePerRound.finale} nomeSquadra={nomeSquadra} />
      )}
    </div>
  );
}

function RoundBlock({
  titolo,
  partite,
  nomeSquadra,
}: {
  titolo: string;
  partite: PartitaItem[];
  nomeSquadra: (id: string) => string;
}) {
  return (
    <div className="card p-0 overflow-hidden">
      <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-accent">
        {titolo}
      </div>
      {partite.map((p) => (
        <PartitaRow key={p.id} partita={p} nomeSquadra={nomeSquadra} />
      ))}
    </div>
  );
}

function SupercoppaSection({
  competizioneId,
  nomeSquadra,
}: {
  competizioneId: string;
  nomeSquadra: (id: string) => string;
}) {
  const { data: partite } = usePartiteCompetizione(competizioneId);
  const partitaSC = partite?.find((p) => p.fase === "supercoppa") ?? partite?.[0] ?? null;

  if (!partitaSC) {
    return (
      <div className="card text-center text-fg-muted">
        {tr("Partita di Supercoppa non ancora generata.")}
      </div>
    );
  }

  return (
    <div className="card p-0 overflow-hidden">
      <div className="bg-primary-deep/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-accent">
        {tr("Supercoppa")}
      </div>
      <PartitaRow partita={partitaSC} nomeSquadra={nomeSquadra} />
    </div>
  );
}

function PartitaRow({
  partita: p,
  nomeSquadra,
}: {
  partita: PartitaItem;
  nomeSquadra: (id: string) => string;
}) {
  const giocata = p.punteggio_casa !== null && p.punteggio_casa !== undefined;
  const inner = (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-primary/10 last:border-b-0 text-sm">
      <span className="text-[10px] text-fg-muted uppercase tracking-wider w-32 shrink-0">
        {labelFase(p.fase)}
      </span>
      <span className="flex-1 truncate text-right font-bold">{nomeSquadra(p.squadra_casa_id)}</span>
      <span className="text-center font-mono tabular-nums w-16">
        {giocata ? (
          <span className="text-fg">
            {p.gol_casa} - {p.gol_trasferta}
          </span>
        ) : (
          <span className="text-fg-muted">vs</span>
        )}
      </span>
      <span className="flex-1 truncate font-bold">{nomeSquadra(p.squadra_trasferta_id)}</span>
      {giocata && (
        <span className="text-[10px] text-fg-muted font-mono w-20 text-right shrink-0">
          {Number(p.punteggio_casa).toFixed(1)} · {Number(p.punteggio_trasferta).toFixed(1)}
        </span>
      )}
    </div>
  );
  return (
    <Link to={`/partita/${p.id}`} className="block hover:bg-primary/5 transition">
      {inner}
    </Link>
  );
}
